"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { debounce } from "@mui/material";
import {
  Box,
  TextField,
  Stack,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Pagination,
  useTheme,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import type { SettingsLocation } from "models/lookupTypes";
import type { WorkOrder, SegmentSchedulingStatus } from "./lib/types";
import {
  DEFAULT_SEGMENT_SCHEDULING_FILTERS,
  SEGMENT_SCHEDULING_STATUS_OPTIONS,
} from "./lib/utils/segment-scheduling-utils";
import { WorkOrderItem } from "./work-order-item";
import type { DragData } from "./lib/hooks/use-drag-drop";
import {
  sidebarCollapsedStyles,
  sidebarExpandedStyles,
  sidebarHeaderPrimaryStyles,
  sidebarToggleButtonStyles,
  sidebarScrollbarStyles,
} from "./schedular-styles";
import { fadeInUp } from "components/common/global-style/GlobalStyling";

interface WorkOrdersSidebarProps {
  workOrders: WorkOrder[];
  segmentParts: any[];
  reservedCapacity: Map<string, number>;
  isBrokenMap: Map<string, boolean>;
  locations?: SettingsLocation[];
  onDragStart: (e: React.DragEvent, data: DragData) => void;
  onDragEnd: (e: React.DragEvent) => void;
  isSegmentBroken?: (segmentId: string) => boolean;
  onUpdateSegmentHours?: (segmentId: string, newHours: number) => void;
  onBreakSegment?: (segmentId: string) => void;
  onConcurrencyError?: (message: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  resetSearchTrigger?: number;
  pageNumber?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  onSearchChange?: (searchString: string) => void;
  initialSearchValue?: string;
  segmentSchedulingStatuses?: SegmentSchedulingStatus[];
  onSegmentSchedulingStatusesChange?: (statuses: SegmentSchedulingStatus[]) => void;
}

export function WorkOrdersSidebar({
  workOrders,
  reservedCapacity,
  isBrokenMap,
  locations = [],
  isSegmentBroken,
  onDragStart,
  onDragEnd,
  onUpdateSegmentHours,
  onBreakSegment,
  onConcurrencyError,
  collapsed = false,
  onToggleCollapse,
  resetSearchTrigger,
  pageNumber = 1,
  totalPages = 0,
  totalCount = 0,
  onPageChange,
  loading = false,
  onSearchChange,
  initialSearchValue = "",
  segmentSchedulingStatuses = DEFAULT_SEGMENT_SCHEDULING_FILTERS,
  onSegmentSchedulingStatusesChange,
}: WorkOrdersSidebarProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearchValue);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      onSearchChange?.(searchValue);
    }, 500), // 500ms debounce delay
    [onSearchChange],
  );
//need to search something
  // Reset search when trigger changes
  // CRITICAL: Only call onSearchChange if it's not already being reset by parent
  // This prevents duplicate fetchWorkOrders calls when reset button is pressed
  useEffect(() => {
    if (resetSearchTrigger !== undefined && resetSearchTrigger > 0) {
      setSearchQuery("");
      // Don't call onSearchChange here - the parent's reset handler will handle fetching
      // This useEffect only resets the UI state, not the data fetch
    }
  }, [resetSearchTrigger]); // Removed onSearchChange from dependencies to prevent duplicate calls

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const toggleSegmentStatus = (status: SegmentSchedulingStatus) => {
    const current = segmentSchedulingStatuses ?? [];
    const next = current.includes(status)
      ? current.filter((item) => item !== status)
      : [...current, status];
    onSegmentSchedulingStatusesChange?.(next);
  };

  const getReservedHours = (workOrderId: string) => {
    return reservedCapacity.get(workOrderId) || 0;
  };

  if (collapsed) {
    return (
      <Paper elevation={0} sx={sidebarCollapsedStyles}>
        <Tooltip title="Expand Work Orders" placement="right">
          <IconButton onClick={onToggleCollapse} sx={sidebarToggleButtonStyles}>
            <ChevronRight />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...sidebarExpandedStyles, pb: 2, animation: `${fadeInUp} 0.5s ease-out 0.2s both`, }} >
      <Box
        sx={{
          ...sidebarHeaderPrimaryStyles,
          p: 2,
          bgcolor: isDarkMode ? "#424242" : "primary.main",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "white" }}>
            Work Orders
          </Typography>
          <Tooltip title="Collapse Sidebar" placement="left">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{
                ...sidebarToggleButtonStyles,
                color: "white",
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(255, 255, 255, 0.1)",
                  color: "white",
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search work orders..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "action.active" }} />
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "background.paper",
            },
          }}
        />
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
          {SEGMENT_SCHEDULING_STATUS_OPTIONS.map((option) => {
            const selected = segmentSchedulingStatuses.includes(option.value);
            return (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                clickable
                variant={selected ? "filled" : "outlined"}
                onClick={() => toggleSegmentStatus(option.value)}
                sx={
                  isDarkMode
                    ? {
                        color: selected ? "white" : "rgba(255,255,255,0.92)",
                        borderColor: selected
                          ? "transparent"
                          : "rgba(255,255,255,0.45)",
                        bgcolor: selected
                          ? "primary.dark"
                          : "rgba(255,255,255,0.08)",
                        fontWeight: selected ? 700 : 500,
                        "&:hover": {
                          bgcolor: selected
                            ? "primary.dark"
                            : "rgba(255,255,255,0.16)",
                        },
                      }
                    : {
                        // Bright theme: selected chips need high contrast on primary.main
                        color: selected ? "primary.main" : "rgba(255,255,255,0.95)",
                        borderColor: selected
                          ? "common.white"
                          : "rgba(255,255,255,0.55)",
                        borderWidth: selected ? 2 : 1,
                        bgcolor: selected
                          ? "common.white"
                          : "rgba(255,255,255,0.12)",
                        fontWeight: selected ? 700 : 500,
                        boxShadow: selected
                          ? "0 1px 4px rgba(0,0,0,0.18)"
                          : "none",
                        "&:hover": {
                          bgcolor: selected
                            ? "common.white"
                            : "rgba(255,255,255,0.22)",
                          borderColor: "common.white",
                        },
                      }
                }
              />
            );
          })}
        </Stack>
      </Box>

      <Stack sx={{ ...sidebarScrollbarStyles, p: 1.5, flex: 1, minHeight: 0, animation: `${fadeInUp} 0.5s ease-out 0.2s both`, }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <>
            {workOrders.map((order) => (
              <WorkOrderItem
                key={order.id}
                workOrder={order}
                reserved={getReservedHours(order.id)}
                isBroken={isBrokenMap.get(order.id) || false}
                locations={locations}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                isSegmentBroken={isSegmentBroken}
                onUpdateSegmentHours={onUpdateSegmentHours}
                onBreakSegment={(segmentId) => onBreakSegment?.(segmentId)}
                onConcurrencyError={onConcurrencyError}
              />
            ))}

            {workOrders.length === 0 && !loading && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No work orders found
                </Typography>
              </Box>
            )}
          </>
        )}
      </Stack>

      {/* Pagination Controls */}
      {(() => {
        const calculatedTotalPages = totalPages || Math.ceil(totalCount / 15);
        const shouldShowPagination =
          calculatedTotalPages > 1 && totalCount > 0 && !loading;

        return shouldShowPagination ? (
          <Box
            sx={{
              p: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Pagination
              count={calculatedTotalPages}
              page={pageNumber}
              onChange={(_, page) => onPageChange?.(page)}
              size="small"
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>
        ) : null;
      })()}
    </Paper>
  );
}
